import {useCallback, useEffect, useMemo} from 'react';
import {requestContext} from '../../../../common/requestContext';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {ReceiveFreeTicketDialog} from '../../../../design-system/organisms/ReceiveFreeTicketDialog/ReceiveFreeTicketDialog';
import {
  ContentCheckFreeTicket,
  Maybe,
  TicketKind,
} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useContentMyTicketQuery} from '../../../../wrapper/ticketWrapper';
import {freeTicketReceived} from '../../../buy/ticket/state/ticketSlice';
import {getFreeTicketDialogInfoFunction} from '../util/getFreeTicketDialogInfo';

/**
 * 콘텐츠홈에 접근할 때 마다 신규혜택으로 받은 무료 이용권이 있는지 체크합니다. (ticket/free_check API 호출)
 * API 단어는 free_check 고 GET 메소드를 이용하지만 실제 이 API 를 호출해야만 무료 이용권이 발급됩니다. (API 요청)
 * 그렇기 때문에 ticket/free_check API 호출 결과로 무료 이용권이 있다면 ticket/my 를 한번 더 호출해줘야 합니다
 */
export function ContentReceiveFreeTicketContainer({
  seriesId,
  contentCheckFreeTicket,
}: {
  seriesId: number;
  contentCheckFreeTicket: Maybe<ContentCheckFreeTicket>;
}) {
  const freeTicketList = contentCheckFreeTicket?.list;

  const dispatch = useTypedDispatch();
  const contentTitle = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview?.content?.title;

  const handleConfirm = useCallback(() => {
    dispatch(freeTicketReceived({seriesId}));
  }, [dispatch, seriesId]);

  const {openDialog, title, highlight, rentSum, expireAt} = useMemo(() => {
    return getFreeTicketDialogInfoFunction(freeTicketList || []);
  }, [freeTicketList]);

  const {isSuccess, refetch: refetchMyTicket} = useContentMyTicketQuery(
    {
      seriesId,
      includeWaitfree: true,
    },
    {enabled: false}
  );
  useEffect(() => {
    // 무료 티켓을 받았다면 ticket/my 를 한번 더 호출해 이용권 개수 동기화를 해줍니다(ios 와 동일)
    // 무료 티켓을 받기 전에 ticket/my를 먼저 요청한 경우, refetchMyTicket가 다시 돌아도 처음 요청한 응답이 내려오기 때문에 요청이 완료된 이후에 다시 refetch를 할 수 있도록 합니다.
    if (isSuccess) {
      refetchMyTicket();
    }
  }, [isSuccess, refetchMyTicket]);

  return (
    <ReceiveFreeTicketDialog
      open={!!openDialog}
      ticketKind={rentSum ? TicketKind.Rent : TicketKind.Own}
      isMobile={requestContext.requestInfo.display.isMobile}
      title={title || ''}
      contentTitle={contentTitle || ''}
      highlight={highlight || ''}
      ticketExpireAt={expireAt}
      handleConfirm={handleConfirm}
    />
  );
}
